import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { onAuthStateChanged, signOut } from "firebase/auth"; 
import { doc, onSnapshot } from "firebase/firestore"; 
import { auth, db } from './firebase'; 

// ⬇️ [SECTION: IMPORTS] - (استيراد المكونات والصفحات الملكية لأكاديمية النور)
import Navbar from './components/Navbar';
import Sidebar from './components/Sidebar';
import Dashboard from './pages/Teacher/Dashboard';
import TokenManager from './pages/Teacher/TokenManager'; 
import LessonsManager from './pages/Teacher/LessonsManager';
import StudentManager from './pages/Teacher/StudentManager';
import QuizManager from './pages/Teacher/QuizManager'; 
import Chat from './pages/Teacher/Chat'; // ملف جات المدرس المحدث 🛡️
import Profile from './pages/Teacher/Profile'; 
import Login from './pages/Auth/Login'; 
import StudentDashboard from './pages/Student/StudentDashboard'; 
import CourseView from './pages/Student/CourseView'; 
import ActivateCode from './pages/Student/ActivateCode'; 
import StudentSchedule from './pages/Student/StudentSchedule'; 
import StudentQuizzes from './pages/Student/StudentQuizzes'; 
import MyCourses from './pages/Student/MyCourses'; 
import StudentChat from './pages/Student/StudentChat'; // ملف جات الطالب المحدث ✨
import StudentProfile from './pages/Student/StudentProfile'; 
import { 
    ExclamationTriangleIcon, ShieldExclamationIcon, LockClosedIcon, 
    ArrowPathIcon, PlusCircleIcon, ShareIcon, EllipsisVerticalIcon, TicketIcon,
    ArrowLeftOnRectangleIcon, SparklesIcon, DevicePhoneMobileIcon, XMarkIcon, ArrowUpTrayIcon,
    CodeBracketIcon
} from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';
import './App.css'; 

// ⬇️ [SECTION: GLOBAL_STYLES] - (نظام الزجاج الملكي والحجب الشامل المطور بواسطة البارون)
const globalStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700;900&display=swap');
  
  :root { 
    --ios-blue: #007AFF; 
    --ios-bg: #F2F2F7; 
    --text-primary: #1C1C1E; 
    --glass-bg: rgba(255, 255, 255, 0.75);
    --glass-border: rgba(255, 255, 255, 0.4);
  }

  [data-theme='dark'] { 
    --ios-bg: #030303 !important; 
    --text-primary: #FFFFFF !important; 
    --glass-bg: rgba(15, 15, 15, 0.7) !important;
    --glass-border: rgba(255, 255, 255, 0.08) !important;
  }
  
  body, html, #root { 
    font-family: 'Cairo', sans-serif !important; 
    background-color: var(--ios-bg) !important; 
    color: var(--text-primary) !important;
    margin: 0; padding: 0;
    overflow-x: hidden !important;
    min-height: 100vh;
    width: 100%;
    transition: background-color 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .sidebar-container { flex-shrink: 0 !important; z-index: 40; height: 100%; }

  .main-layout {
    display: flex; width: 100%; height: calc(100vh - 64px); 
    position: relative; overflow: hidden;
  }

  .content-area {
    vertical-align: top;
    flex: 1; width: 100%; height: 100%;
    overflow-y: auto; overflow-x: hidden;
    position: relative; z-index: 1;
    transition: transform 0.4s ease;
  }

  .ios-glass {
    background: var(--glass-bg) !important;
    backdrop-filter: blur(35px) saturate(200%) !important;
    border: 1px solid var(--glass-border) !important;
  }

  .royal-smoked-glass {
    position: fixed !important; 
    inset: 0 !important; 
    z-index: 2147483647 !important; 
    backdrop-filter: blur(65px) saturate(180%) brightness(0.7) !important;
    background: rgba(0, 0, 0, 0.55) !important; 
    display: flex !important; 
    align-items: center !important; 
    justify-content: center !important;
    padding: 20px;
    width: 100vw !important;
    height: 100vh !important;
  }

  .custom-scroll::-webkit-scrollbar { width: 5px; }
  .custom-scroll::-webkit-scrollbar-thumb { background: var(--ios-blue); border-radius: 10px; }
  
  .modal-overlay {
    position: fixed; inset: 0; z-index: 100000;
    display: flex; align-items: center; justify-content: center;
    padding: 1.5rem; backdrop-filter: blur(20px); background: rgba(0,0,0,0.6);
  }

  .animate-spin-slow { animation: spin 3s linear infinite; }
  @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(-360deg); } }
`;

// ⬇️ [SECTION: STUDENT_ACCESS_GUARD] - حماية الدخول للمشتركين فقط
const StudentAccessGuard = ({ user, children }) => {
    const isAllowed = user?.isSubscribed === true || user?.isActivated === true || user?.activeCourse;
    return isAllowed ? children : <Navigate to="/student" replace />;
};

// ⬇️ [SECTION: COURSE_PROTECTED_ROUTE] - نظام حماية الكورسات الذكي
const CourseProtectedRoute = ({ user, children }) => {
    const currentPath = window.location.pathname;
    const clean = (str) => (str || "").toString().trim().replace(/[أإآا]/g, "ا").toLowerCase();
    const subType = clean(user?.subscriptionType || "");
    const activeC = clean(user?.activeCourse || "");

    const hasFullAccess = subType.includes("كامل") || 
                         activeC.includes("كامل") || 
                         subType.includes("كورسين") || 
                         user?.isSubscribed === true ||
                         (user?.isCourse1Active && user?.isCourse2Active);

    if (hasFullAccess) return children;

    if (currentPath.includes("course1") || currentPath.includes("first")) {
        const hasC1 = user?.isCourse1Active === true || subType.includes("اول") || subType.includes("1") || activeC.includes("اول");
        return hasC1 ? children : <Navigate to="/student/courses" />;
    }

    if (currentPath.includes("course2") || currentPath.includes("second")) {
        const hasC2 = user?.isCourse2Active === true || subType.includes("ثاني") || subType.includes("2") || activeC.includes("ثاني");
        return hasC2 ? children : <Navigate to="/student/courses" />;
    }

    return children;
};

// ⬇️ [SECTION: MAIN_APP_COMPONENT] - المكون الرئيسي للنظام
const App = () => {
    const [user, setUser] = useState(null); 
    const [loading, setLoading] = useState(true); 
    const [blockAlert, setBlockAlert] = useState(false);
    
    // ⬇️ [STATES: SYSTEM_MESSAGES] - الموقع الآن يدير التحديث البرمجي فقط
    const [updateAvailable, setUpdateAvailable] = useState(false);
    const [waitingWorker, setWaitingWorker] = useState(null);
    const [showActivationPopup, setShowActivationPopup] = useState(false); 
    const [deviceType, setDeviceType] = useState('desktop');
    
    const updateShownRef = useRef(false);

    // 🛡️ [SECTION: SECURITY_LOCK] - حماية المنصة من النسخ والكونسول
    useEffect(() => {
        const preventSecurityBreach = (e) => {
            if (e.type === "contextmenu") { e.preventDefault(); return false; }
            if (e.keyCode === 123 || (e.ctrlKey && e.shiftKey && (e.keyCode === 73 || e.keyCode === 74 || e.keyCode === 67)) || (e.ctrlKey && e.keyCode === 85)) {
                e.preventDefault(); return false;
            }
        };
        document.addEventListener("contextmenu", preventSecurityBreach);
        document.addEventListener("keydown", preventSecurityBreach);
        return () => {
            document.removeEventListener("contextmenu", preventSecurityBreach);
            document.removeEventListener("keydown", preventSecurityBreach);
        };
    }, []);

    // 🔄 [SECTION: AL-BARON_PWA_FORCE_SYNC] - إدارة تحديثات الموقع فورياً لجميع الأجهزة (Deploy Sync)
    useEffect(() => {
        const uaString = navigator.userAgent.toLowerCase();
        const isMobile = /iphone|ipad|ipod|android/.test(uaString);
        setDeviceType(isMobile ? 'mobile' : 'desktop');

        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.getRegistration().then(reg => {
                if (reg) {
                    // ⭐ [THE_FIX]: إجبار السيرفس وركر على الفحص الآن فور فتح التطبيق لضمان التحديث في الموبايل واللابتوب
                    reg.update(); 

                    // الفحص الاعتيادي للتحديثات المعلقة
                    if (reg.waiting && !updateShownRef.current) {
                                      setWaitingWorker(reg.waiting);
                                     // ⬇️ هنا ضفنا تأخير لمدة 5 ثواني (تقدر تغير الـ 5000 حسب رغبتك)
                                       setTimeout(() => {
                                       setUpdateAvailable(true);
                                     }, 5000); 
                                     updateShownRef.current = true;
                                }

                    reg.addEventListener('updatefound', () => {
                        const newWorker = reg.installing;
                        newWorker.addEventListener('statechange', () => {
                            if (newWorker.state === 'installed' && navigator.serviceWorker.controller && !updateShownRef.current) {
                                setWaitingWorker(newWorker);
                                setUpdateAvailable(true);
                                updateShownRef.current = true;
                            }
                        });
                    });
                }
            });
            // ابحث عن هذا الجزء داخل الـ useEffect الخاص بالـ serviceWorker
/*let refreshing = false;
navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (refreshing) return;
    refreshing = true;
    // window.location.reload(); ⬅️ سوي تعطيل (Comment) لهذا السطر فوراً
});*/
        }
    }, []);

    const handleUpdate = () => {
    if (waitingWorker) { 
        waitingWorker.postMessage({ type: 'SKIP_WAITING' }); 
    }
    setUpdateAvailable(false);
    
    // 🚀 التعديل الجوهري هنا: نحدث الصفحة يدوياً بعد ثانية واحدة من ضغط الزر
    setTimeout(() => {
        window.location.reload();
    }, 1000);
};

    // 🔄 [SECTION: AUTH_SYNC] - مزامنة المصادقة والبيانات الملكية (تحسين سرعة اللودنج هنا ✅)
    useEffect(() => {
        let unsubDoc = () => {};
        const unsubscribeAuth = onAuthStateChanged(auth, async (firebaseUser) => {
            if (firebaseUser) {
                const userDocRef = doc(db, "users", firebaseUser.uid);
                unsubDoc = onSnapshot(userDocRef, (docSnap) => {
                    if (docSnap.exists()) {
                        const userData = docSnap.data();
                        if (userData?.status === 'blocked') {
                            setBlockAlert(true);
                            setTimeout(() => { signOut(auth); setUser(null); setBlockAlert(false); }, 5000);
                            return;
                        }
                        setUser({ uid: firebaseUser.uid, email: firebaseUser.email, ...userData });
                    }
                    // 🚀 حل مشكلة التأخير: ننهي حالة الـ Loading فور استلام البيانات
                    setLoading(false); 
                }, (error) => {
                    setLoading(false); 
                });
            } else { 
                setUser(null); 
                setLoading(false); 
            }
        });
        return () => { unsubscribeAuth(); unsubDoc(); };
    }, []);

    // ⏳ [SECTION: LOADING_SCREEN] - واجهة التحميل الملكية بنظام iOS (Al-Baron Style)
    if (loading) return (
        <div className="h-screen w-screen flex items-center justify-center bg-[#F2F2F7] dark:bg-[#030303]">
            <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                className="w-16 h-16 border-[4px] border-[#007AFF]/20 border-t-[#007AFF] rounded-full shadow-[0_0_15px_rgba(0,122,255,0.3)]"
            />
        </div>
    );

    return (
        <Router>
            <style>{globalStyles}</style>
            
            {/* ⬇️ [SECTION: SYSTEM_NOTIFICATIONS] - إدارة رسالة التحديث فقط هنا (تحديث : تم إضافة تحسينات جديدة) */}
            <AnimatePresence>
                {updateAvailable && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className={`fixed inset-0 z-[2147483647] flex justify-center p-6 bg-black/60 backdrop-blur-xl ${deviceType === 'desktop' ? 'items-center' : 'items-start pt-10'}`}
                    >
                        <motion.div initial={{ scale: 0.9, y: deviceType === 'desktop' ? 0 : -20 }} animate={{ scale: 1, y: 0 }}
                            className="ios-glass p-8 rounded-[40px] border border-white/20 bg-white/90 dark:bg-[#1C1C1E]/95 shadow-2xl max-w-[340px] w-full text-center space-y-6"
                        >
                            <ArrowPathIcon className="w-12 h-12 text-[#007AFF] animate-spin-slow mx-auto" />
                            <h3 className="text-2xl font-black text-gray-900 dark:text-white font-cairo">تحديث</h3>
                            <button onClick={handleUpdate} className="w-full py-4 bg-[#007AFF] text-white rounded-2xl font-black font-cairo text-sm"> تم إضافة تحسينات و خدمات امنية  جديدة النسخة الجديدة جاهزة ✅</button>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* 🛡️ [SECTION: THE_BARON_LOCKDOWN] - حماية المنصة عند عدم التفعيل */}
            <AnimatePresence>
                {user && user.role === 'student' && !user.isActivated && !user.isSubscribed && window.location.pathname !== '/student/activate' && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="royal-smoked-glass">
                        <AnimatePresence mode="wait">
                            {!showActivationPopup ? (
                                <motion.div key="lock-card" initial={{ scale: 0.95, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.9, opacity: 0, y: -20 }} className="ios-glass p-1 rounded-[35px] shadow-[0_35px_100px_rgba(0,0,0,0.6)] max-w-[310px] w-full border border-white/20">
                                    <div className="bg-white/95 dark:bg-[#1C1C1E]/90 p-6 rounded-[33px] text-center space-y-5 flex flex-col items-center">
                                        <div className="w-14 h-14 bg-[#007AFF]/10 rounded-[20px] flex items-center justify-center shadow-inner"><LockClosedIcon className="w-7 h-7 text-[#007AFF]" /></div>
                                        <div className="space-y-1">
                                            <h3 className="text-[16px] font-black text-slate-900 dark:text-white font-cairo whitespace-nowrap">الوصول محمي 🛡️</h3>
                                            <p className="text-[11px] font-bold text-gray-500 dark:text-gray-400 font-cairo whitespace-nowrap">المنصة مقفلة حالياً، يرجى التفعيل.</p>
                                        </div>
                                        <div className="w-full space-y-3">
                                            <button onClick={() => setShowActivationPopup(true)} className="w-full py-3 bg-[#007AFF] text-white rounded-xl font-black shadow-lg active:scale-95 transition-all font-cairo text-[12px] flex items-center justify-center gap-2"><TicketIcon className="w-4 h-4" />التوجة إلى بطاقة التفعيل 🚀</button>
                                            <button onClick={() => signOut(auth)} className="w-full py-3 bg-red-500/10 text-red-500 rounded-xl font-black border border-red-500/20 active:scale-95 transition-all font-cairo text-[12px] flex items-center justify-center gap-2">تسجيل الخروج</button>
                                        </div>
                                    </div>
                                </motion.div>
                            ) : (
                                <motion.div key="activation-card" initial={{ scale: 0.9, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.9, opacity: 0, y: -20 }} className="ios-glass p-1 rounded-[35px] shadow-[0_35px_100px_rgba(0,0,0,0.6)] max-w-[340px] w-full border border-white/20">
                                    <div className="bg-white/95 dark:bg-[#1C1C1E]/90 p-6 rounded-[33px] text-center space-y-4">
                                        <ActivateCode user={user} isPopup={true} onClose={() => setShowActivationPopup(false)} />
                                        <button onClick={() => setShowActivationPopup(false)} className="text-[11px] font-bold text-gray-400 font-cairo hover:text-[#007AFF] transition-colors">رجوع للخلف</button>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* 🔑 [SECTION: ROUTES_CONTROLLER] - نظام إدارة المسارات */}
            <Routes>
                {/* 🔒 [SECTION: AUTH_ROUTE] */}
                <Route path="/login" element={!user ? <Login /> : <Navigate to={user.role === 'teacher' ? "/admin" : "/student"} />} />
                
                <Route path="*" element={
                    user ? (
                        <div dir="rtl" className="relative h-screen w-full flex flex-col bg-[var(--ios-bg)] overflow-hidden transition-colors duration-500">
                            
                            <div className="relative z-10 flex flex-col h-screen">
                                <Navbar user={user} />
                                <div className="main-layout flex-1 flex overflow-hidden">
                                    <div className="sidebar-container h-full"><Sidebar userRole={user.role} /></div>
                                    <main className="content-area custom-scroll">
                                        <div className="px-2 lg:px-8 max-w-[1600px] mx-auto min-h-full py-6">
                                            <Routes>
                                                {/* 🛠️ [SECTION: TEACHER_ROUTES] */}
                                                {user.role === 'teacher' ? (
                                                    <>
                                                        <Route path="/admin" element={<Dashboard user={user} />} />
                                                        <Route path="/admin/tokens" element={<TokenManager user={user} />} />
                                                        <Route path="/admin/lessons" element={<LessonsManager user={user} />} />
                                                        <Route path="/admin/students" element={<StudentManager user={user} />} /> 
                                                        <Route path="/admin/quizzes" element={<QuizManager user={user} />} /> 
                                                        <Route path="/admin/profile" element={<Profile user={user} />} />
                                                    </>
                                                ) : (
                                                    <>
                                                        {/* 🎓 [SECTION: STUDENT_ROUTES] */}
                                                        <Route path="/student" element={<StudentDashboard user={user} />} />
                                                        <Route path="/student/activate" element={<ActivateCode user={user} />} />
                                                        
                                                        {/* 🛡️ [SECTION: PROTECTED_STUDENT_ROUTES] */}
                                                        <Route path="/student/courses" element={<StudentAccessGuard user={user}><MyCourses user={user} /></StudentAccessGuard>} />
                                                        <Route path="/student/schedule" element={<StudentAccessGuard user={user}><StudentSchedule user={user} /></StudentAccessGuard>} />
                                                        <Route path="/student/quizzes" element={<StudentAccessGuard user={user}><StudentQuizzes user={user} /></StudentAccessGuard>} />
                                                        
                                                        <Route path="/student/profile" element={<StudentProfile user={user} />} />
                                                        <Route path="/student/course/:type" element={<StudentAccessGuard user={user}><CourseProtectedRoute user={user}><CourseView user={user} /></CourseProtectedRoute></StudentAccessGuard>} />
                                                    </>
                                                )}

                                                {/* 💬 [SECTION: GLOBAL_CHAT_ROUTE] */}
                                                <Route path="/chat" element={ (user.role === 'teacher' || user.isSubscribed === true || user.activeCourse) ? (user.role === 'teacher' ? <Chat user={user} /> : <StudentChat user={user} />) : <Navigate to="/student/activate" /> } />

                                                {/* 🛤️ [SECTION: Fallback Routes] */}
                                                <Route path="/" element={<Navigate to={user.role === 'teacher' ? "/admin" : "/student"} />} />
                                                <Route path="*" element={<Navigate to="/" />} />
                                            </Routes>
                                        </div>
                                    </main>
                                </div>
                            </div>
                        </div>
                    ) : <Navigate to="/login" />
                } />
            </Routes>
        </Router>
    );
};

export default App;